import { useCallback, useContext } from 'react';
import { toast } from 'react-toastify';
import AuthContext from '../context/authContext';
import useHttp from './useHttp';

const useCustomerExperienceFeedback = () => {
  const authCtx = useContext(AuthContext);
  const { sendRequest } = useHttp();

  const getCustomerFeedback = useCallback(
    async ({
      status = null,
      entertainmentId = null,
      branchId = null,
      pendingApproval = false,
      page = 1,
      limit = 50,
    } = {}) => {
      try {
        const params = new URLSearchParams();
        if (status) params.append('status', status);
        if (entertainmentId) params.append('entertainmentId', entertainmentId);
        if (branchId) params.append('branchId', branchId);
        if (pendingApproval) params.append('pendingApproval', 'true');
        params.append('page', String(page));
        params.append('limit', String(limit));

        return await sendRequest({
          url: `/api/v1/experienceFeedback?${params.toString()}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden des Kundenfeedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const getFeedbackById = useCallback(
    async (feedbackId) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden des Feedbacks');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const updateStatus = useCallback(
    async ({ feedbackId, status }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/status`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { status },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  /**
   * Marks the customer as called and optionally saves a call note.
   */
  const updateCustomerCalled = useCallback(
    async ({ feedbackId, customerCalled, callNote = null }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/customerCalled`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { customerCalled, callNote },
        });
      } catch (err) {
        toast.error('Fehler beim Aktualisieren des Anruf-Status');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const generateAiSuggestion = useCallback(
    async ({ feedbackId }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/aiSuggestion`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        });
      } catch (err) {
        toast.error('Fehler beim Generieren des KI-Vorschlags');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  const sendResponse = useCallback(
    async ({ feedbackId, responseText, courtesyVoucherValue }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/respond`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { responseText, courtesyVoucherValue },
        });
      } catch (err) {
        toast.error('Fehler beim Senden der Antwort');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Confirms the AI's auto-resolve assessment: the backend dispatches the AI
  // response + recommended voucher and resolves the feedback.
  const acceptAiTriage = useCallback(
    async ({ feedbackId }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/aiTriage/accept`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
        });
      } catch (err) {
        toast.error('Fehler beim Bestätigen der KI-Einschätzung');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Rejects the AI's auto-resolve assessment; the feedback stays in the manual queue.
  const rejectAiTriage = useCallback(
    async ({ feedbackId, rejectionNote = null }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/aiTriage/reject`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { rejectionNote },
        });
      } catch (err) {
        toast.error('Fehler beim Ablehnen der KI-Einschätzung');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Admin audit list of feedback whose AI auto-resolve assessment was rejected.
  const getRejectedAiTriage = useCallback(
    async ({ page = 1, limit = 50 } = {}) => {
      try {
        const params = new URLSearchParams();
        params.append('page', String(page));
        params.append('limit', String(limit));
        return await sendRequest({
          url: `/api/v1/experienceFeedback/aiTriage/rejected?${params.toString()}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der abgelehnten KI-Einschätzungen');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Overrides the classification/category of a feedback item.
  const setClassification = useCallback(
    async ({ feedbackId, classification }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/${feedbackId}/classification`,
          method: 'PATCH',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { classification },
        });
      } catch (err) {
        toast.error('Fehler beim Ändern der Klassifikation');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Remaining monthly goodwill budget of a branch, for the budget bar in the
  // response composer. Silent on error — the bar simply stays hidden.
  const getCourtesyVoucherBudget = useCallback(
    async ({ branchId }) => {
      return await sendRequest({
        url: `/api/v1/experienceFeedback/voucherBudget?branchId=${encodeURIComponent(branchId)}`,
        method: 'GET',
        headers: authCtx.getAuthHeaders(),
      });
    },
    [authCtx, sendRequest]
  );

  // Answers parked for approval: own requests plus the ones the user may decide.
  const getVoucherApprovals = useCallback(
    async ({ status = 'pending' } = {}) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/voucherApprovals?status=${encodeURIComponent(status)}`,
          method: 'GET',
          headers: authCtx.getAuthHeaders(),
        });
      } catch (err) {
        toast.error('Fehler beim Laden der offenen Gutschein-Freigaben');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Approving dispatches the parked answer immediately (voucher + mail).
  const approveVoucherRequest = useCallback(
    async ({ requestId, comment = null }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/voucherApprovals/${requestId}/approve`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { comment },
        });
      } catch (err) {
        toast.error('Fehler beim Freigeben des Kulanzgutscheins');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Rejecting hands the case back to the employee; the requester may also
  // withdraw their own request this way.
  const rejectVoucherRequest = useCallback(
    async ({ requestId, comment = null }) => {
      try {
        return await sendRequest({
          url: `/api/v1/experienceFeedback/voucherApprovals/${requestId}/reject`,
          method: 'POST',
          headers: { ...authCtx.getAuthHeaders(), 'Content-Type': 'application/json' },
          body: { comment },
        });
      } catch (err) {
        toast.error('Fehler beim Ablehnen der Gutschein-Freigabe');
        throw err;
      }
    },
    [authCtx, sendRequest]
  );

  // Overdue (>24h unanswered) feedback for the reminder pop-ups. Silent on error
  // so the background watcher never surfaces its own toasts.
  const getOverdueFeedback = useCallback(async () => {
    return await sendRequest({
      url: `/api/v1/experienceFeedback/overdue`,
      method: 'GET',
      headers: authCtx.getAuthHeaders(),
    });
  }, [authCtx, sendRequest]);

  return {
    getCustomerFeedback,
    getFeedbackById,
    updateStatus,
    updateCustomerCalled,
    generateAiSuggestion,
    sendResponse,
    acceptAiTriage,
    rejectAiTriage,
    getRejectedAiTriage,
    setClassification,
    getCourtesyVoucherBudget,
    getVoucherApprovals,
    approveVoucherRequest,
    rejectVoucherRequest,
    getOverdueFeedback,
  };
};

export default useCustomerExperienceFeedback;
